import React, { ReactNode } from 'react';
import { useIsInViewPort, useSetDisabledNav } from '../hooks';
import styles from '../SimpleCarousel.module.scss';
import { NavDirections, SlideConfig } from '../types';

const SLIDE_CLASS_MAP: { [slideCount: string]: string } = {
    '1-tablet': styles.slideTabletOne,
    '2-tablet': styles.slideTabletTwo,
    '3-tablet': styles.slideTabletThree,
    '4-tablet': styles.slideTabletFour,
    '5-tablet': styles.slideTabletFive,
    '6-tablet': styles.slideTabletSix,
    '1-desktop': styles.slideDesktopOne,
    '2-desktop': styles.slideDesktopTwo,
    '3-desktop': styles.slideDesktopThree,
    '4-desktop': styles.slideDesktopFour,
    '5-desktop': styles.slideDesktopFive,
    '6-desktop': styles.slideDesktopSix,
};

export const Slide: React.FunctionComponent<SlideProps> = (props) => {
    const {
        slidesVisible,
        lazilyRenderOffScreenElements,
        slideNumber,
        setDisabledNav,
        childrenLength,
        fullyEnableNavigation = false,
        isFirstPage = false,
        isLastPage = false,
    } = props;

    const slideRef = React.useRef<HTMLDivElement>(null);
    const isLastSlide = slideNumber === childrenLength - 1;
    const isFirstSlide = slideNumber === 0;

    const { hasEnteredViewport, isVisible } = useIsInViewPort({
        ref: slideRef,
        trackVisibility: isLastSlide || isFirstSlide,
    });
    useSetDisabledNav({
        isLastSlide,
        isFirstSlide,
        isVisible,
        setDisabledNav,
        fullyEnableNavigation,
        isFirstPage,
        isLastPage,
    });

    const slideClassSelector = slidesVisible;
    const desktopSelector = `${slideClassSelector.desktop}-desktop`;
    const tabletSelector = `${slideClassSelector.tablet}-tablet`;

    const slideClasses = `${styles.slide} ${SLIDE_CLASS_MAP[desktopSelector]} ${SLIDE_CLASS_MAP[tabletSelector]}`;

    /**
     * Prevent rendering this slide if
     * should lazily render, is offscreen,
     * and should not initially be visible
     */
    const shouldNotRenderChild =
        lazilyRenderOffScreenElements &&
        slideNumber + 1 > slidesVisible.desktop &&
        !hasEnteredViewport;

    return (
        <div
            className={slideClasses}
            ref={slideRef}
            data-testid="simple-carousel-slide"
        >
            {shouldNotRenderChild ? null : props.children}
        </div>
    );
};

interface SlideProps {
    slidesVisible: SlideConfig;
    slideNumber: number;
    childrenLength: number;
    lazilyRenderOffScreenElements?: boolean;
    setDisabledNav: (direction?: NavDirections) => void;
    /***
     * Disable side navigation icons
     */
    fullyEnableNavigation?: boolean;
    isFirstPage?: boolean;
    isLastPage?: boolean;
    children?: ReactNode;
}
